import { useCallback, useContext, useState } from 'react';
import unauthenticatedFetch from 'services/rest/unauthenticatedFetch';
import { RETRY_INTERVAL } from 'shared/constants/auth';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { getItem, removeItem, setItem } from 'shared/utils/asyncStorage';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface CreateSessionResponse {
  sessionToken: string | null;
  statusCode: number;
}

const createSession = async (
  lambdaUrl: string,
): Promise<CreateSessionResponse> => {
  const headers = await getLambdaHeaders();
  try {
    const response = await unauthenticatedFetch(`${lambdaUrl}/create-session`, {
      method: 'POST',
      cache: 'no-cache',
      headers,
    });
    return response.json() as Promise<CreateSessionResponse>;
  } catch (error) {
    logErrorWithContext('createSession error: ' + error, {
      featureArea: FeatureArea.AUTHENTICATION,
      responsibleTeam: ResponsibleTeam.FRAUD,
      severity: ErrorSeverity.HIGH,
    });
    return { sessionToken: null, statusCode: 500 };
  }
};

function useCreateSession() {
  const [varoSessionId, setVaroSessionId] = useState<string>('');
  const { currentEnv, getLambdaUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const lambdaUrl = getLambdaUrl(currentEnv);
  const [createSessionError, setCreateSessionError] = useState<string>('');

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const getVaroSessionId = useCallback(async (): Promise<string> => {
    try {
      if (varoSessionId) {
        return Promise.resolve(varoSessionId);
      }

      const storedSessionId = await getItem(STORAGE_KEYS.X_VARO_SESSION);
      if (storedSessionId) {
        setVaroSessionId(storedSessionId);
        return Promise.resolve(storedSessionId);
      }

      logBreadcrumb({ message: 'Fetching session token', level: 'info' });

      let sessionToken: string | null = null;
      let statusCode: number | null = null;
      let retries = 0;
      const maxRetries = 3;
      const retryInterval = RETRY_INTERVAL;

      while (retries < maxRetries) {
        try {
          ({ sessionToken, statusCode } = await createSession(lambdaUrl));

          if (sessionToken) {
            break;
          }

          if (statusCode !== 200) {
            setCreateSessionError(
              `Create session request failure with status code ${statusCode} and Retry attempt ${retries} of ${maxRetries}`,
            );
          }
        } catch (error) {
          logErrorWithContext('getVaroSessionId error: ' + error, {
            featureArea: FeatureArea.AUTHENTICATION,
            responsibleTeam: ResponsibleTeam.FRAUD,
            severity: ErrorSeverity.HIGH,
          });
          setCreateSessionError(
            `Create session request failure ${error} Retry attempt ${retries} of ${maxRetries}`,
          );
          return Promise.resolve('');
        }

        retries++;
        logBreadcrumb({
          message: `Retry attempt ${retries} of ${maxRetries}`,
          level: 'info',
        });
        if (retries < maxRetries) {
          await delay(retryInterval);
        }
      }

      await setItem(STORAGE_KEYS.X_VARO_SESSION, sessionToken ?? '');
      setVaroSessionId(sessionToken ?? '');
      return Promise.resolve(sessionToken ?? '');
    } catch (error) {
      logBreadcrumb({
        message: `getVaroSessionId failure ${error}`,
        level: 'error',
      });
      return Promise.resolve('');
    }
  }, [varoSessionId, setVaroSessionId, lambdaUrl, setCreateSessionError]);

  const clearVaroSessionId = useCallback(async () => {
    await removeItem(STORAGE_KEYS.X_VARO_SESSION);
    setVaroSessionId('');
    await resetReviewPromptStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varoSessionId]);

  return {
    varoSessionId,
    setVaroSessionId,
    createSessionError,
    getVaroSessionId,
    clearVaroSessionId,
  };
}

export const resetReviewPromptStatus = async () => {
  try {
    await removeItem('hasShownReview');
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'SecureStore.removeItemSecureStore' + error,
    });
  }
};

export default useCreateSession;
